/**
 * Telemetry Store
 *
 * Zustand store for accumulating Firestore and RTDB telemetry data between heartbeats.
 * Tracks per-page operation counts (reads, writes, deletes) and distinguishes
 * between cache hits (free) and server reads (costly).
 *
 * RTDB telemetry is tracked separately since billing is bandwidth-based (not per-read).
 * We track snapshot count/bytes downloaded and write count/bytes uploaded per page.
 *
 * Data is sent with each heartbeat (60s) and then cleared for the next interval.
 */

import { create } from 'zustand';

// =============================================================================
// Types
// =============================================================================

/** Per-page statistics (using short keys to minimize payload size) */
export interface PageStats {
  r: number;   // reads
  w: number;   // writes
  d: number;   // deletes
  ch: number;  // cache hits
  sr: number;  // server reads
}

/** Telemetry payload sent with heartbeat */
interface TelemetryPayload {
  intervalMs: number;
  activeListenerCount: number;
  totalReads: number;
  totalWrites: number;
  totalDeletes: number;
  totalCacheHits: number;
  totalServerReads: number;
  pages: Record<string, PageStats>;
}

/** Per-page RTDB statistics (short keys to minimize payload size) */
export interface RTDBPageStats {
  sc: number;  // snapshot count (initial syncs + deltas)
  sd: number;  // snapshot bytes downloaded (only counted for initial syncs — see `isc`)
  wc: number;  // write count
  wu: number;  // write bytes uploaded
  isc: number; // initial-sync snapshot count (subset of sc; `sd` is honest bandwidth for these)
}

/** A captured client-side error, buffered until the next heartbeat */
interface ClientErrorEntry {
  level: 'error' | 'rejection' | 'react' | 'chunk';
  message: string;
  stack?: string;
  page: string;
  timestamp: number;  // epoch ms of first occurrence
  count: number;      // consecutive occurrences collapsed into this entry
}

/** A page navigation with dwell time, buffered until the next heartbeat */
interface NavEventEntry {
  page: string;
  enteredAt: number;  // epoch ms
  dwellMs: number;
}

/** Max buffered entries per interval — defensive bound mirrored server-side */
export const MAX_BUFFERED_ERRORS = 100;
export const MAX_BUFFERED_NAV_EVENTS = 200;

/** RTDB telemetry payload sent with heartbeat */
interface RTDBTelemetryPayload {
  intervalMs: number;
  activeRtdbListeners: number;
  totalSnapshots: number;
  totalBytesDownloaded: number;
  totalWrites: number;
  totalBytesUploaded: number;
  /**
   * Subset of totalSnapshots that were initial-sync events (full-node
   * stringify). `totalSnapshots - totalInitialSyncSnapshots` is the delta
   * (edit) event count, which contributes 0 to totalBytesDownloaded since
   * delta events are not re-stringified (see rtdbSnapshotTelemetry.ts).
   */
  totalInitialSyncSnapshots: number;
  pages: Record<string, RTDBPageStats>;
}

/** Store state shape */
interface TelemetryState {
  // Per-page Firestore operation counts
  pageStats: Record<string, PageStats>;

  // Active Firestore subscriptions (paths)
  activeListeners: Set<string>;

  // Per-page RTDB operation counts
  rtdbPageStats: Record<string, RTDBPageStats>;

  // Active RTDB subscriptions (paths)
  activeRtdbListeners: Set<string>;

  // When the current interval started
  intervalStart: number;

  // Current page path for attribution
  currentPage: string;

  // Client-side errors captured this interval (flushed on heartbeat)
  clientErrors: ClientErrorEntry[];

  // Page navigations captured this interval (flushed on heartbeat)
  navEvents: NavEventEntry[];
}

/** Store actions */
interface TelemetryActions {
  /**
   * Record a Firestore operation
   * @param action - Type of operation (Read, Write, Subscribe, Unsubscribe, Snapshot)
   * @param path - Firestore path
   * @param source - For snapshots, whether from 'cache' or 'server'
   * @param count - Number of documents (for snapshots with multiple docs)
   */
  recordEvent: (
    action: 'Read' | 'Write' | 'Subscribe' | 'Unsubscribe' | 'Snapshot',
    path: string,
    source?: 'cache' | 'server',
    count?: number
  ) => void;

  /**
   * Track a new active listener
   */
  addListener: (path: string) => void;

  /**
   * Remove an active listener
   */
  removeListener: (path: string) => void;

  /**
   * Set the current page for operation attribution
   */
  setCurrentPage: (page: string) => void;

  /**
   * Get the accumulated telemetry data and reset for next interval
   * Called by usePresenceTracker before sending heartbeat
   */
  getAndClearTelemetry: () => TelemetryPayload;

  /**
   * Record an RTDB operation (Snapshot or Write) with byte size.
   * @param isInitialSync - For 'Snapshot' events only: whether this was the
   *   first snapshot of the listener's lifecycle (vs. a delta event). Ignored
   *   for 'Write' events.
   */
  recordRtdbEvent: (
    action: 'Snapshot' | 'Write',
    path: string,
    bytes: number,
    isInitialSync?: boolean
  ) => void;

  /**
   * Track a new active RTDB listener
   */
  addRtdbListener: (path: string) => void;

  /**
   * Remove an active RTDB listener
   */
  removeRtdbListener: (path: string) => void;

  /**
   * Get the accumulated RTDB telemetry data and reset for next interval
   * Called by usePresenceTracker before sending heartbeat
   */
  getAndClearRtdbTelemetry: () => RTDBTelemetryPayload;

  /**
   * Buffer a captured client-side error. Callable outside React (global
   * window.onerror / unhandledrejection handlers, error boundaries).
   * Oldest entries are dropped past MAX_BUFFERED_ERRORS.
   */
  recordError: (
    level: ClientErrorEntry['level'],
    message: string,
    stack?: string,
    page?: string
  ) => void;

  /** Buffer a page navigation with its dwell time. */
  recordNavEvent: (page: string, enteredAt: number, dwellMs: number) => void;

  /** Snapshot-and-clear buffered client errors (called before heartbeat). */
  getAndClearErrors: () => ClientErrorEntry[];

  /** Snapshot-and-clear buffered navigation events (called before heartbeat). */
  getAndClearNavEvents: () => NavEventEntry[];
}

type TelemetryStore = TelemetryState & TelemetryActions;

// =============================================================================
// Helper Functions
// =============================================================================

/** Create empty page stats */
function createEmptyPageStats(): PageStats {
  return { r: 0, w: 0, d: 0, ch: 0, sr: 0 };
}

/** Get or create page stats for a given page */
function getOrCreatePageStats(
  pageStats: Record<string, PageStats>,
  page: string
): PageStats {
  if (!pageStats[page]) {
    pageStats[page] = createEmptyPageStats();
  }
  return pageStats[page];
}

/** Create empty RTDB page stats */
function createEmptyRtdbPageStats(): RTDBPageStats {
  return { sc: 0, sd: 0, wc: 0, wu: 0, isc: 0 };
}

/** Get or create RTDB page stats for a given page */
function getOrCreateRtdbPageStats(
  rtdbPageStats: Record<string, RTDBPageStats>,
  page: string
): RTDBPageStats {
  if (!rtdbPageStats[page]) {
    rtdbPageStats[page] = createEmptyRtdbPageStats();
  }
  return rtdbPageStats[page];
}

// =============================================================================
// Store
// =============================================================================

export const useTelemetryStore = create<TelemetryStore>((set, get) => ({
  // Initial state
  pageStats: {},
  activeListeners: new Set(),
  rtdbPageStats: {},
  activeRtdbListeners: new Set(),
  intervalStart: Date.now(),
  currentPage: '/',
  clientErrors: [],
  navEvents: [],

  // Actions
  recordEvent: (action, _path, source, count = 1) => {
    set((state) => {
      const currentPage = state.currentPage;
      const newPageStats = { ...state.pageStats };
      const stats = getOrCreatePageStats(newPageStats, currentPage);

      switch (action) {
        case 'Read':
          // Direct reads (getDoc, etc.)
          stats.r += count;
          // Direct reads go to server unless explicitly cached
          if (source === 'cache') {
            stats.ch += count;
          } else {
            stats.sr += count;
          }
          break;

        case 'Write':
          // setDoc, updateDoc, addDoc
          stats.w += count;
          break;

        case 'Snapshot':
          // onSnapshot callbacks - track source
          // count = number of documents in the snapshot (each doc = 1 billable read)
          stats.r += count;
          if (source === 'cache') {
            stats.ch += count;
          } else {
            stats.sr += count;
          }
          break;

        case 'Subscribe':
        case 'Unsubscribe':
          // Handled by addListener/removeListener
          break;
      }

      return { pageStats: newPageStats };
    });
  },

  addListener: (path) => {
    set((state) => {
      const newListeners = new Set(state.activeListeners);
      newListeners.add(path);
      return { activeListeners: newListeners };
    });
  },

  removeListener: (path) => {
    set((state) => {
      const newListeners = new Set(state.activeListeners);
      newListeners.delete(path);
      return { activeListeners: newListeners };
    });
  },

  setCurrentPage: (page) => {
    set({ currentPage: page });
  },

  getAndClearTelemetry: () => {
    const state = get();
    const now = Date.now();
    const intervalMs = now - state.intervalStart;

    // Calculate totals from page stats
    let totalReads = 0;
    let totalWrites = 0;
    let totalDeletes = 0;
    let totalCacheHits = 0;
    let totalServerReads = 0;

    for (const stats of Object.values(state.pageStats)) {
      totalReads += stats.r;
      totalWrites += stats.w;
      totalDeletes += stats.d;
      totalCacheHits += stats.ch;
      totalServerReads += stats.sr;
    }

    const payload: TelemetryPayload = {
      intervalMs,
      activeListenerCount: state.activeListeners.size,
      totalReads,
      totalWrites,
      totalDeletes,
      totalCacheHits,
      totalServerReads,
      pages: state.pageStats,
    };

    // Reset for next interval
    set({
      pageStats: {},
      intervalStart: now,
      // Keep activeListeners and currentPage - they persist across intervals
    });

    return payload;
  },

  recordRtdbEvent: (action, _path, bytes, isInitialSync) => {
    set((state) => {
      const currentPage = state.currentPage;
      const newRtdbPageStats = { ...state.rtdbPageStats };
      const stats = getOrCreateRtdbPageStats(newRtdbPageStats, currentPage);

      switch (action) {
        case 'Snapshot':
          stats.sc += 1;
          stats.sd += bytes;
          if (isInitialSync) {
            stats.isc += 1;
          }
          break;
        case 'Write':
          stats.wc += 1;
          stats.wu += bytes;
          break;
      }

      return { rtdbPageStats: newRtdbPageStats };
    });
  },

  addRtdbListener: (path) => {
    set((state) => {
      const newListeners = new Set(state.activeRtdbListeners);
      newListeners.add(path);
      return { activeRtdbListeners: newListeners };
    });
  },

  removeRtdbListener: (path) => {
    set((state) => {
      const newListeners = new Set(state.activeRtdbListeners);
      newListeners.delete(path);
      return { activeRtdbListeners: newListeners };
    });
  },

  getAndClearRtdbTelemetry: () => {
    const state = get();
    const now = Date.now();
    const intervalMs = now - state.intervalStart;

    // Calculate totals from RTDB page stats
    let totalSnapshots = 0;
    let totalBytesDownloaded = 0;
    let totalWrites = 0;
    let totalBytesUploaded = 0;
    let totalInitialSyncSnapshots = 0;

    for (const stats of Object.values(state.rtdbPageStats)) {
      totalSnapshots += stats.sc;
      totalBytesDownloaded += stats.sd;
      totalWrites += stats.wc;
      totalBytesUploaded += stats.wu;
      totalInitialSyncSnapshots += stats.isc;
    }

    const payload: RTDBTelemetryPayload = {
      intervalMs,
      activeRtdbListeners: state.activeRtdbListeners.size,
      totalSnapshots,
      totalBytesDownloaded,
      totalWrites,
      totalBytesUploaded,
      totalInitialSyncSnapshots,
      pages: state.rtdbPageStats,
    };

    // Reset RTDB stats for next interval
    set({
      rtdbPageStats: {},
      // Keep activeRtdbListeners - they persist across intervals
    });

    return payload;
  },

  recordError: (level, message, stack, page) => {
    if (!message) return;
    set((state) => {
      const resolvedPage = page || state.currentPage;

      // Collapse consecutive identical errors (same level/message/page) into a
      // single entry with a repeat count, so an error loop (e.g. a failing
      // Firestore retry) can't evict unrelated errors from the buffer or
      // flood Session History with identical rows. Timestamp stays at first
      // occurrence; the first stack is kept.
      const prev = state.clientErrors;
      const last = prev[prev.length - 1];
      if (last && last.level === level && last.message === message && last.page === resolvedPage) {
        return {
          clientErrors: [...prev.slice(0, -1), { ...last, count: last.count + 1 }],
        };
      }

      const entry: ClientErrorEntry = {
        level,
        message,
        stack,
        page: resolvedPage,
        timestamp: Date.now(),
        count: 1,
      };
      // Keep only the most recent MAX_BUFFERED_ERRORS (drop oldest)
      const next = [...prev, entry];
      return {
        clientErrors: next.length > MAX_BUFFERED_ERRORS
          ? next.slice(next.length - MAX_BUFFERED_ERRORS)
          : next,
      };
    });
  },

  recordNavEvent: (page, enteredAt, dwellMs) => {
    if (!page) return;
    set((state) => {
      const next = [...state.navEvents, { page, enteredAt, dwellMs }];
      return {
        navEvents: next.length > MAX_BUFFERED_NAV_EVENTS
          ? next.slice(next.length - MAX_BUFFERED_NAV_EVENTS)
          : next,
      };
    });
  },

  getAndClearErrors: () => {
    const errors = get().clientErrors;
    if (errors.length > 0) set({ clientErrors: [] });
    return errors;
  },

  getAndClearNavEvents: () => {
    const events = get().navEvents;
    if (events.length > 0) set({ navEvents: [] });
    return events;
  },
}));

// =============================================================================
// Accessor for Logger (avoids circular dependency)
// =============================================================================


